<script setup>
import { useRoute } from 'vue-router';
import {computed, inject, onBeforeMount, ref, watch} from "vue";
import {useAuthUserStore} from "../Store/AuthUserStore.js";
import {useSubscriptionStore} from "../Store/SubscriptionStore.js";
import {useToast} from "vue-toast-notification";
import axios from "axios";

const $toast = useToast();
const route = useRoute();
const brandConfig = inject('brandConfig');

const hideSidebarOnClickOnMobile = () => {
    const windowSize = document.documentElement.clientWidth;
    if (windowSize <= 767 && document.body.classList.contains("vertical-sidebar-enable")) {
        document.body.classList.remove("vertical-sidebar-enable");
    }
};

const isActiveMenuParent = (parent) => {
    return parent === route.meta.menu.parent;
};

const isActiveMenuChild = (child) => {
    return child === route.meta.menu.child;
};

const notifications = ref({});
function getNotifications() {
    axios.get('/api/notifications')
        .then(response => {
            notifications.value = response.data?.data ?? {};
        })
        .catch(errors => {
            console.log(errors);
        })
}

const subscriptionStore = useSubscriptionStore();

onBeforeMount(async () => {
    getNotifications();
    await subscriptionStore.fetchAccessibleModules();
});

//for each center of the authUSer, we add a notification number
const authUserStore = useAuthUserStore();
const authUser = computed(() => authUserStore.authUser);
const isSubscribed = ref(false);

function subscribeToCentersNotificationsChannel() {
    if (authUser.value && Array.isArray(authUser.value.centers)) {
        authUser.value.centers.forEach(center => {
            Echo.channel('notifications_center_' + center.id)
                .listen('CenterNotificationNumberUpdatedEvent', (e) => {
                    getNotifications();
                });
        });
    }
}

function subscribeToUserNotificationsChannel() {
    Echo.channel('notifications_user_' + authUser.value.id)
        .listen('UserNotificationNumberUpdatedEvent', (e) => {
            getNotifications();
        });
}

function subscribeToBackOfficeNotificationsChannel() {
    Echo.channel('notifications_bo')
        .listen('BackOfficeNotificationNumberUpdatedEvent', (e) => {
            getNotifications();
        });
}

function subscribeToEveryoneNotificationsChannel() {
    Echo.channel('notifications_everyone')
        .listen('EveryoneNotificationUpdatedEvent', (e) => {
            getNotifications();
        })
        .listen('NewAppVersionEvent', (e) => {
            $toast.warning('Nouvelle version de l\'application disponible. Veuillez cliquer ici ou vous déconnecter/reconnecter', {
                duration: 0,
                position: 'top',
                dismissible: true,
                pauseOnHover: true,
                closeOnClick: false,
                onDismiss: () => {
                   location.reload();
                }
            });
        });
}

watch(authUser, (newAuthUser) => {
    if (newAuthUser && !isSubscribed.value) {
        subscribeToCentersNotificationsChannel();
        subscribeToUserNotificationsChannel();
        subscribeToEveryoneNotificationsChannel();
        if (newAuthUser.role === 'admin' || newAuthUser.role === 'back_office') {
            subscribeToBackOfficeNotificationsChannel();
        }
        isSubscribed.value = true;
    }
});

</script>

<template>
    <ul class="navbar-nav" id="navbar-nav" data-simplebar>
        <!-- GÉNÉRAL -->
        <li class="menu-title"><span data-key="t-menu">Général</span></li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'dashboard' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('dashboard') }">
                <i class="mdi mdi-view-grid-plus-outline"></i> <span data-key="t-starter">Dashboard</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'users.profile' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('profile') }">
                <i class="mdi mdi-account-circle-outline"></i> <span data-key="t-starter">Mon profil</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'centers.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('centers') }">
                <i class="mdi mdi-office-building-marker-outline"></i> <span data-key="t-starter">Centres</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'users.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('users') }">
                <i class="mdi mdi-account-multiple-outline"></i> <span data-key="t-starter">Utilisateurs</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'users.management-team' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('management-team') }">
                <i class="mdi mdi-account-star-outline"></i> <span data-key="t-starter">Team {{ brandConfig.companyName }}</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'chiptuning.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('chiptuning') }">
                <i class="mdi mdi-speedometer"></i> <span data-key="t-starter">Reprogrammations</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'statistics.index' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('statistics') }">
                <i class="mdi mdi-chart-bar"></i> <span data-key="t-starter">Statistiques</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="subscriptionStore.billingEnabled">
            <router-link :to="{ name: 'admin.plans.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('admin-plans') }">
                <i class="mdi mdi-credit-card-outline"></i> <span data-key="t-starter">Abonnements</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile" v-if="brandConfig.shopUrl">
            <a :href="brandConfig.shopUrl" target="_blank" rel="noopener noreferrer" class="nav-link menu-link">
                <i class="mdi mdi-cart-outline"></i> <span data-key="t-starter">Shop</span>
                <i class="mdi mdi-open-in-new ms-auto"></i>
            </a>
        </li>

        <!-- GESTION CLIENTS -->
        <li class="menu-title"><span data-key="t-menu">Gestion clients</span></li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'clients.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('clients') }">
                <i class="mdi mdi-account-group-outline"></i> <span data-key="t-starter">Contacts</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'calls.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('calls') }">
                <i class="mdi mdi-phone"></i> <span data-key="t-starter">Liste d'appels</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'client-requests.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('client-requests') && isActiveMenuChild('client-requests.list') }">
                <i class="mdi mdi-car-info"></i> <span data-key="t-starter">Demandes clients</span>
                <span class="badge badge-pill bg-danger" data-key="t-hot" v-if="notifications.notifications_client_requests && notifications.notifications_client_requests > 0">{{ notifications.notifications_client_requests }}</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'client-requests.agenda' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('client-requests') && isActiveMenuChild('client-requests.agenda') }">
                <i class="mdi mdi-calendar"></i> <span data-key="t-starter">Agenda RDV</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'client-request-others.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('client-request-others') }">
                <i class="mdi mdi-folder-information-outline"></i> <span data-key="t-starter">Autres demandes</span>
                <span class="badge badge-pill bg-danger" data-key="t-hot" v-if="notifications.notifications_client_requests_others && notifications.notifications_client_requests_others > 0">{{ notifications.notifications_client_requests_others }}</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'information-requests.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('information-requests') }">
                <i class="mdi mdi-book-information-variant"></i> <span data-key="t-starter">Demandes d'infos</span>
                <span class="badge badge-pill bg-danger" data-key="t-hot" v-if="notifications.notifications_information_requests && notifications.notifications_information_requests > 0">{{ notifications.notifications_information_requests }}</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'quotes.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('quotes') }">
                <i class="mdi mdi-file-sign"></i> <span data-key="t-starter">Devis clients</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'invoices.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('invoices') }">
                <i class="mdi mdi-bank"></i> <span data-key="t-starter">Factures clients</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'products.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('products') }">
                <i class="mdi mdi-package-variant"></i> <span data-key="t-starter">Produits & Services</span>
            </router-link>
        </li>

        <!-- SUPPORT -->
        <li class="menu-title"><span data-key="t-menu">Support</span></li>

        <li class="nav-item">
            <a class="nav-link menu-link" href="#tickets" data-bs-toggle="collapse" role="button" aria-controls="tickets" :class="{ 'active': isActiveMenuParent('tickets') }" :aria-expanded="isActiveMenuParent('tickets')">
                <i class="mdi mdi-tooltip-plus-outline"></i> <span data-key="t-layouts">Tickets</span>
                <span class="badge badge-pill bg-danger" data-key="t-hot" v-if="notifications.notifications_tickets && notifications.notifications_tickets > 0">{{ notifications.notifications_tickets }}</span>
            </a>
            <div class="collapse menu-dropdown" id="tickets" :class="{ 'show': isActiveMenuParent('tickets') }">
                <ul class="nav nav-sm flex-column">
                    <li class="nav-item" @click="hideSidebarOnClickOnMobile">
                        <router-link :to="{ name: 'tickets.list' }" class="nav-link" :class="{ 'active': isActiveMenuChild('tickets') }" >Tous les tickets</router-link>
                    </li>
                    <li class="nav-item" @click="hideSidebarOnClickOnMobile">
                        <router-link :to="{ name: 'tickets-categories.list' }" class="nav-link" :class="{ 'active': isActiveMenuChild('tickets-categories') }" >Catégories</router-link>
                    </li>
                </ul>
            </div>
        </li>

        <li class="nav-item">
            <a class="nav-link menu-link" href="#helpArticles" data-bs-toggle="collapse" role="button" aria-controls="helpArticles" :class="{ 'active': isActiveMenuParent('help-articles') }" :aria-expanded="isActiveMenuParent('help-articles')">
                <i class="mdi mdi-help-circle-outline"></i> <span data-key="t-layouts">Centre d'aide</span>
                <span class="badge badge-pill bg-info" v-if="notifications.help_article_available">New</span>
            </a>
            <div class="collapse menu-dropdown" id="helpArticles" :class="{ 'show': isActiveMenuParent('help-articles') }">
                <ul class="nav nav-sm flex-column">
                    <li class="nav-item" @click="hideSidebarOnClickOnMobile">
                        <router-link :to="{ name: 'help-articles.list' }" class="nav-link" :class="{ 'active': isActiveMenuChild('help-articles') }" >Toutes les fiches</router-link>
                    </li>
                    <li class="nav-item" @click="hideSidebarOnClickOnMobile">
                        <router-link :to="{ name: 'help-article-categories.list' }" class="nav-link" :class="{ 'active': isActiveMenuChild('help-article-categories') }" >Catégories</router-link>
                    </li>
                </ul>
            </div>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'default-messages.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('default-messages') }">
                <i class="mdi mdi-message-bookmark-outline"></i> <span data-key="t-starter">Messages prédéfinis</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'invoices-zoho.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('invoices-zoho') }">
                <i class="mdi mdi-sticker-text-outline"></i> <span data-key="t-starter">Factures Zoho</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'hubspot-sync-issues.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('hubspot-sync-issues') }">
                <i class="mdi mdi-sync-alert"></i> <span data-key="t-starter">Erreurs HubSpot</span>
                <span class="badge badge-pill bg-danger" data-key="t-hot" v-if="notifications.hubspot_sync_issues && notifications.hubspot_sync_issues > 0">{{ notifications.hubspot_sync_issues }}</span>
            </router-link>
        </li>

        <!-- COMMUNICATION -->
        <li class="menu-title"><span data-key="t-menu">Communication</span></li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'announcements.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('announcements') }">
                <i class="mdi mdi-bullhorn-outline"></i> <span data-key="t-starter">Bandeaux d'annonce</span>
            </router-link>
        </li>

        <li class="nav-item">
            <a class="nav-link menu-link" href="#news" data-bs-toggle="collapse" role="button" aria-controls="news" :class="{ 'active': isActiveMenuParent('news') }" :aria-expanded="isActiveMenuParent('news')">
                <i class="mdi mdi-newspaper"></i> <span data-key="t-layouts">Actualités</span>
                <span class="badge badge-pill bg-info" v-if="notifications.news_available">New</span>
            </a>
            <div class="collapse menu-dropdown" id="news" :class="{ 'show': isActiveMenuParent('news') }">
                <ul class="nav nav-sm flex-column">
                    <li class="nav-item" @click="hideSidebarOnClickOnMobile">
                        <router-link :to="{ name: 'news.list' }" class="nav-link" :class="{ 'active': isActiveMenuChild('news') }" >Toutes les actualités</router-link>
                    </li>
                    <li class="nav-item" @click="hideSidebarOnClickOnMobile">
                        <router-link :to="{ name: 'news-categories.list' }" class="nav-link" :class="{ 'active': isActiveMenuChild('news-categories') }" >Catégories</router-link>
                    </li>
                </ul>
            </div>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'events.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('events') }">
                <i class="mdi mdi-calendar"></i> <span data-key="t-starter">Évènements</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'surveys.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('surveys') }">
                <i class="mdi mdi-thumbs-up-down-outline"></i> <span data-key="t-starter">Formulaires</span>
            </router-link>
        </li>

        <li class="nav-item" @click="hideSidebarOnClickOnMobile">
            <router-link :to="{ name: 'social-network-media.list' }" class="nav-link menu-link" :class="{ 'active': isActiveMenuParent('social-network-media') }">
                <i class="mdi mdi-instagram"></i> <span data-key="t-starter">Biblio. médias</span>
                <span class="badge badge-pill bg-success" v-if="notifications.social_network_media_available">New</span>
            </router-link>
        </li>
    </ul>
</template>

<style scoped>

</style>
